import { useState, type FormEvent } from 'react';
import type { ProfileSetup, UsernameAvailability } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { EMPTY_PROFILE, ProfileFields, profileProblem, toProfileSetup, type ProfileValues } from './profile-fields';

type CompleteProps = {
  // the player already has a name (for example after a new version of the Terms): only ask the rest
  username: string | null;
  checkAvailability: (username: string) => Promise<UsernameAvailability>;
  onSubmit: (body: ProfileSetup) => Promise<void>;
  onSignOut: () => void;
  // filled in when the email sign-up already collected the answers but sending them failed
  initial?: ProfileValues;
  initialError?: FriendlyError | null;
};

// the one-time step after a first Google sign-in (or when a legal document changes): nothing else in the
// game opens until it is done, and the API enforces the same rules
export function CompleteProfileCard({
  username,
  checkAvailability,
  onSubmit,
  onSignOut,
  initial,
  initialError,
}: CompleteProps) {
  const [values, setValues] = useState<ProfileValues>(initial ?? { ...EMPTY_PROFILE, username: username ?? '' });
  const [availability, setAvailability] = useState<UsernameAvailability | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(initialError ?? null);
  const askUsername = !username;
  const problem = profileProblem(values, askUsername ? availability : { available: true }, askUsername);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (problem) return setFailure({ kind: 'validation', message: problem, retryable: false });
    setBusy(true);
    setFailure(null);
    try {
      await onSubmit(toProfileSetup({ ...values, username: username ?? values.username }));
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="sign-in-card" onSubmit={(event) => void submit(event)} aria-labelledby="complete-profile-title">
      <h1 id="complete-profile-title">{askUsername ? 'Complete your profile' : 'Before you carry on'}</h1>
      <p className="sign-in-lead">
        {askUsername
          ? 'One quick step before you play. We only ask once.'
          : 'Our documents have been updated. Please confirm your date of birth and agree to continue.'}
      </p>
      <ProfileFields
        values={values}
        onChange={setValues}
        checkAvailability={askUsername ? checkAvailability : undefined}
        onAvailability={setAvailability}
        idPrefix="complete"
      />
      {failure && <ErrorNotice error={failure} />}
      <Button type="submit" className="sign-in-primary" disabled={busy}>
        {busy ? 'Saving…' : 'Continue to London Life'}
      </Button>
      <div className="sign-in-links">
        <button type="button" onClick={onSignOut}>
          Log me out
        </button>
      </div>
    </form>
  );
}

// shown to a suspended or banned account: the reason, when it ends, and how to appeal
export function AccountHeldCard({
  hold,
  onSignOut,
}: {
  hold: { kind: 'suspended' | 'banned'; until: string | null; reason: string | null };
  onSignOut: () => void;
}) {
  const until = hold.until
    ? new Date(hold.until).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  return (
    <div className="sign-in-card" role="alert">
      <h1>{hold.kind === 'banned' ? 'This account has been banned' : 'This account is suspended'}</h1>
      <p className="sign-in-lead">
        {hold.kind === 'banned'
          ? 'You can no longer play London Life with this account.'
          : `You can play again from ${until}.`}
        {hold.reason && (
          <>
            {' '}
            Reason: <strong>{hold.reason}</strong>.
          </>
        )}
      </p>
      <p className="sign-in-lead">
        If you think this is a mistake, email <a href="mailto:support@londonlifegame.com">support@londonlifegame.com</a>{' '}
        from the address you signed up with.
      </p>
      <Button type="button" className="sign-in-primary" onClick={onSignOut}>
        Log me out
      </Button>
    </div>
  );
}

// a warning from the moderators, shown once
export function WarningCard({ text, createdAt, onSeen }: { text: string; createdAt: string; onSeen: () => void }) {
  return (
    <div className="sign-in-card" role="alertdialog" aria-labelledby="warning-title">
      <h1 id="warning-title">A warning from the London Life team</h1>
      <p className="sign-in-lead">{text}</p>
      <p className="sign-in-lead">
        Sent {new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}. Repeated problems can
        lead to a suspension or a ban.
      </p>
      <Button type="button" className="sign-in-primary" onClick={onSeen}>
        I understand
      </Button>
    </div>
  );
}

// shown to an account blocked because its date of birth was under 18
export function AgeRestrictedCard({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="sign-in-card" role="alert">
      <h1>London Life is for adults</h1>
      <p className="sign-in-lead">
        You need to be 18 or over to play. If you entered your date of birth by mistake, email{' '}
        <a href="mailto:support@londonlifegame.com">support@londonlifegame.com</a> from the address you signed up with
        and we will review it.
      </p>
      <Button type="button" className="sign-in-primary" onClick={onSignOut}>
        Log me out
      </Button>
    </div>
  );
}

// shown to a player who signed up with email and has not clicked the link yet: playing needs a verified email
// (it protects starting money from throwaway accounts), so the creator waits here with a way forward
export function VerifyEmailCard({
  email,
  onResend,
  onCheck,
  onSignOut,
}: {
  email: string | null;
  onResend: () => Promise<void>;
  onCheck: () => Promise<boolean>;
  onSignOut: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const run = async (work: () => Promise<string | null>) => {
    setBusy(true);
    setFailure(null);
    setNote(null);
    try {
      setNote(await work());
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="sign-in-card" aria-labelledby="verify-email-title">
      <h1 id="verify-email-title">Verify your email to start</h1>
      <p className="sign-in-lead">
        We sent a link to <strong>{email ?? 'your email address'}</strong>. Open it, then come back and press the button
        below. Check your spam folder if it has not arrived.
      </p>
      {note && (
        <p role="status" className="sign-in-lead">
          {note}
        </p>
      )}
      {failure && <ErrorNotice error={failure} />}
      <div className="sign-in-actions">
        <Button
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () =>
              (await onCheck()) ? null : 'Not verified yet. Open the link in the email, then try again.',
            )
          }
        >
          I have verified my email
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => void run(async () => (await onResend(), 'A new link is on its way.'))}
        >
          Send the link again
        </Button>
        <Button type="button" variant="ghost" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </section>
  );
}

// the player's account: who they are, sign out, and deleting the account for good (typed confirmation)
// deleting the account for good, confirmed by typing the @username (shared by the account card and the
// profile's settings)
export function DeleteAccountForm({
  username,
  onDelete,
  onCancel,
}: {
  username: string | null;
  onDelete: (confirmUsername: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const expected = username ? `@${username}` : 'DELETE';
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      await onDelete(typed);
    } catch (error) {
      setFailure(describeError(error));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(event) => void submit(event)} className="account-delete">
      <h2>Delete your account for good</h2>
      <p className="sign-in-lead">
        Your character, money and progress end now and cannot be brought back. Your personal details are erased; records
        of money movements stay without your name, because the game's books must balance. You can sign up again later as
        a new player.
      </p>
      <label>
        Type <strong>{expected}</strong> to confirm
        <input value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
      </label>
      {failure && <ErrorNotice error={failure} />}
      <div className="sign-in-actions">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Keep my account
        </Button>
        <Button
          type="submit"
          variant="destructive"
          disabled={busy || typed.replace(/^@/, '').toLowerCase() !== expected.replace(/^@/, '').toLowerCase()}
        >
          {busy ? 'Deleting…' : 'Delete my account'}
        </Button>
      </div>
    </form>
  );
}

export function AccountCard({
  username,
  email,
  characterName,
  onSignOut,
  onDelete,
  onClose,
  onCookieSettings,
}: {
  username: string | null;
  email: string | null;
  characterName: string | null;
  onSignOut: () => void;
  onDelete: (confirmUsername: string) => Promise<void>;
  onClose: () => void;
  // reopens the cookie banner so the player can change their choice
  onCookieSettings?: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  return (
    <section className="sign-in-card" aria-labelledby="account-title">
      <h1 id="account-title">Your account</h1>
      <dl className="account-facts">
        <dt>Username</dt>
        <dd>{username ? `@${username}` : 'Not chosen yet'}</dd>
        <dt>Email</dt>
        <dd>{email ?? '—'}</dd>
        <dt>Character</dt>
        <dd>{characterName ?? 'Not created yet'}</dd>
      </dl>
      {!deleting ? (
        <div className="sign-in-actions">
          <Button type="button" onClick={onClose}>
            Back to London
          </Button>
          <Button type="button" variant="outline" onClick={onSignOut}>
            Sign out
          </Button>
          {onCookieSettings && (
            <Button type="button" variant="outline" onClick={onCookieSettings}>
              Cookie settings
            </Button>
          )}
          <Button type="button" variant="ghost" className="danger-text" onClick={() => setDeleting(true)}>
            Delete my account
          </Button>
        </div>
      ) : (
        <DeleteAccountForm username={username} onDelete={onDelete} onCancel={() => setDeleting(false)} />
      )}
    </section>
  );
}
